import { reactive, ref, type UnwrapNestedRefs } from 'vue';
import type { VideoJsPlayer } from 'video.js';
import { downloadMovieFile, takeScreenshotOfVidWithCameraPosition } from 'src/lib/moviePlayerHelper';
import type { LeftRight, MoviePlayerCompareModeState } from 'src/models/moviePlayer';

interface Deps {
  getVjsForVid: (vid: string) => VideoJsPlayer | undefined;
}

interface MoviePlayerCompareModeDownloadContext {
  isDownloadingScreenshot: boolean;
  isDownloadingMovieFile: boolean;
  takeScreenshot: (lr: LeftRight, vid: string) => Promise<void>;
  tryDownload: (lr: LeftRight) => Promise<void>;
}

export function useMoviePlayerCompareModeDownload(
  state: UnwrapNestedRefs<MoviePlayerCompareModeState>,
  deps: Deps,
): UnwrapNestedRefs<MoviePlayerCompareModeDownloadContext> {
  const isDownloadingScreenshot = ref(false);
  const isDownloadingMovieFile = ref(false);

  const takeScreenshot = async(lr: LeftRight, vid: string) => {
    // 連打防止
    if (isDownloadingScreenshot.value) return;

    const movie = state[lr].movieList.current.currentMovie;
    const dt = state[lr].geo.currentMovieCurrentDt;
    const vjs = deps.getVjsForVid(vid);
    if (!movie || !dt || !vjs) return;

    isDownloadingScreenshot.value = true;
    takeScreenshotOfVidWithCameraPosition(movie, dt, vid, vjs, state.ui.selectedCameraPosition);
    // スクショを保存するだけなのですぐ終わるはずだが、
    // 念の為1秒待つ
    setTimeout(() => {
      isDownloadingScreenshot.value = false;
    }, 1000);
  };
  const tryDownload = async(lr: LeftRight) => {
    const currentMovie = state[lr].movieList.current.currentMovie;
    // 連打防止
    if (isDownloadingMovieFile.value || !currentMovie) return;

    isDownloadingMovieFile.value = true;
    try {
      await downloadMovieFile(currentMovie, state.ui.selectedCameraPosition);
    } finally {
      // この時点でダウンロードは完了しているはずだが、
      // 念の為もう1秒待つ
      setTimeout(() => {
        isDownloadingMovieFile.value = false;
      }, 1000);
    }
  };

  return reactive({ isDownloadingScreenshot, isDownloadingMovieFile, takeScreenshot, tryDownload });
}
